// Consolidado da Fiscalização (#404): filtros ↔ URL, rótulos e as abas da
// planilha. Puro — a definição dos números mora no backend
// (services/paineis/fiscalizacao.js + montaPainelFiscalizacao.js).
//
// limpaCampo, rotuloInfracao e opcoesDePosto são os do Painel de Autos: mesmo
// comportamento nos dois painéis (corrigir lá corrige aqui).
export { limpaCampo, rotuloInfracao, opcoesDePosto, notaSemAutos } from '../AutosInfracao/filtrosPainel';

// O que veio na URL vai como veio: valor inválido segue para o backend, que
// responde 400 dizendo o campo (mesmo comportamento do Painel de Autos, #395).
export function filtrosDaUrl(search) {
  const p = new URLSearchParams(search || '');
  return { de: p.get('de') || '', ate: p.get('ate') || '', posto: p.get('posto') || '' };
}

export function paramsDosFiltros(f) {
  const r = {};
  ['de', 'ate', 'posto'].forEach((k) => { if (f[k]) r[k] = f[k]; });
  return r;
}

export function queryDosFiltros(f) {
  const s = new URLSearchParams(paramsDosFiltros(f)).toString();
  return s ? `?${s}` : '';
}

// 2026-10-01 → 01/10/2026
export const dataBR = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '');
export const rotuloPeriodo = ({ de, ate }) => (de === ate ? dataBR(de) : `${dataBR(de)} a ${dataBR(ate)}`);

// Quantidade lançada: até 3 casas (kg com gramas, L inteiro).
export const formataQuantidade = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 });

// "1.500,3 kg · 80 L" — quantidades de um grupo, uma por unidade.
export const formataQuantidades = (qs) =>
  (qs || []).map((q) => `${formataQuantidade(q.quantidade)} ${q.unidade}`).join(' · ');

// 12345678000199 → 12.345.678/0001-99; o que não tem 14 dígitos vai como veio.
export const formataCnpj = (c) => {
  const d = String(c || '');
  return d.length === 14 ? `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}` : d;
};

// Período que alcança o mês da última carga da Receita: os autos ainda não
// estão todos na base, e "0 autos" seria lido como queda de arrecadação.
export const avisoReceita = ({ completa_ate: ate }) => (ate
  ? `A base da Receita só está completa até ${dataBR(ate)}: autos lavrados depois disso ainda não foram importados, e os valores deste período estão incompletos.`
  : 'Não há registro de carga da base da Receita: os valores podem estar incompletos.');

const formataReaisTexto = (v) =>
  `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Parte do total da Receita que não é de posto do SIG (Auditoria Especializada
// e "Outros"). Em jun/2026 foram 45% do valor em 26 autos: sem o aviso, o total
// era lido como valor da operação ao lado das autuações do SIG.
export const notaForaDosPostos = ({ fora_dos_postos: fora, total }) => {
  if (!fora || !fora.autos) return null;
  const pct = total ? ` (${((100 * fora.total) / total).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% do valor)` : '';
  return `${fora.autos} ${fora.autos === 1 ? 'auto' : 'autos'} somando ${formataReaisTexto(fora.total)}${pct} vêm da Auditoria `
    + 'Especializada ou de RFs fora dos postos — não são de posto do SIG e não entram no filtro de posto.';
};

// Quanto das autuações fiscais traz, no "Nº (Autuação / RO)", um nº de auto
// que existe na base da Receita — o único vínculo entre as duas bases.
export const textoVinculo = ({ autuacoes_fiscais: total, com_numero: comNumero, casam_receita: casam }) =>
  `${casam} de ${total} autuações fiscais trazem no campo "Nº (Autuação / RO)" um nº de auto que existe na base da Receita`
  + ` (${comNumero} têm o campo preenchido).`;

// Nome do arquivo: consolidado-fiscalizacao_2026-10-01_2026-10-31[_posto].xlsx
export const nomeDoArquivo = ({ periodo, posto }) => {
  const sufixo = posto
    ? `_${posto.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, '-').toLowerCase()}`
    : '';
  return `consolidado-fiscalizacao_${periodo.de}_${periodo.ate}${sufixo}.xlsx`;
};

// Objeto em dois níveis, achatado para a planilha: uma linha de grupo (nível 1)
// por unidade, seguida das linhas dos itens (nível 2) que dependem dele.
export function linhasDeObjeto(porGrupo) {
  const linhas = [];
  (porGrupo || []).forEach((g) => {
    g.quantidades.forEach((q, i) => linhas.push({
      'Nível': 1, Grupo: g.grupo, Objeto: '(total do grupo)',
      'Infrações': i === 0 ? g.ocorrencias : null, Quantidade: q.quantidade, Unidade: q.unidade,
    }));
    g.itens.forEach((it) => linhas.push({
      'Nível': 2, Grupo: g.grupo, Objeto: it.item, 'Infrações': it.ocorrencias, Quantidade: it.quantidade, Unidade: it.unidade,
    }));
  });
  return linhas;
}

// Abas da planilha: [nome, linhas]. Números saem como número (não texto
// formatado), para quem for somar no Excel. `listas` (placas e empresas
// completas) vem da rota /listas; sem ela, as abas usam as mais abordadas.
export function abasDaPlanilha(d, listas) {
  const recorte = `${rotuloPeriodo(d.periodo)} · ${d.posto || 'todos os postos'}`;
  const placas = (listas && listas.placas) || d.veiculos.mais_abordadas;
  const empresas = (listas && listas.empresas) || d.empresas.mais_abordadas;
  const rec = d.autos_receita;
  const resumo = [
    { Indicador: 'Recorte', Valor: recorte },
    { Indicador: 'Abordagens', Valor: d.abordagens },
    { Indicador: 'Veículos abordados (registros)', Valor: d.veiculos.total },
    { Indicador: 'Veículos abordados (placas distintas)', Valor: d.veiculos.placas_distintas },
    { Indicador: 'Placas abordadas mais de uma vez', Valor: d.veiculos.placas_repetidas },
    { Indicador: 'Condutores (registros)', Valor: d.abordados.condutores },
    { Indicador: 'Ajudantes — demais abordados do veículo (registros)', Valor: d.abordados.ajudantes },
    { Indicador: 'Pessoas distintas (CPF)', Valor: d.abordados.cpfs_distintos },
    { Indicador: 'Ajudantes', Valor: 'O formulário registra basicamente o condutor: ajudantes quase nunca são lançados.' },
    { Indicador: 'Empresas fiscalizadas (CNPJs distintos)', Valor: d.empresas.cnpjs_distintos },
    { Indicador: 'Empresas abordadas mais de uma vez', Valor: d.empresas.cnpjs_repetidos },
    { Indicador: 'Documentos com 11 dígitos (CPF no campo de CNPJ)', Valor: d.empresas.cpfs_na_lista },
    { Indicador: 'Infrações aplicadas (ocorrências autuadas)', Valor: d.infracoes.autuadas },
    { Indicador: 'Infrações aplicadas — fiscais', Valor: d.infracoes.fiscais },
    { Indicador: 'Infrações aplicadas — policiais', Valor: d.infracoes.policiais },
    { Indicador: 'Ocorrências registradas (com e sem autuação)', Valor: d.infracoes.registradas },
    { Indicador: 'Autos da Receita lavrados (por RF)', Valor: rec.autos },
    { Indicador: 'Autos da Receita — imposto (R$)', Valor: rec.imposto },
    { Indicador: 'Autos da Receita — multa (R$)', Valor: rec.multa },
    { Indicador: 'Autos da Receita — multa formal (R$)', Valor: rec.multa_formal },
    { Indicador: 'Autos da Receita — total (R$)', Valor: rec.total },
    { Indicador: 'Autos da Receita — fora dos postos (Auditoria Especializada / Outros) (R$)', Valor: rec.fora_dos_postos.total },
    { Indicador: 'Vínculo infração ↔ auto da Receita', Valor: textoVinculo(rec.vinculo) },
    { Indicador: 'Quantidade', Valor: 'Quantidade da mercadoria lançada na infração, na unidade lançada — não é a carga total do veículo.' },
  ];
  if (rec.incompleta) resumo.push({ Indicador: 'Atenção', Valor: avisoReceita(rec) });
  if (notaForaDosPostos(rec)) resumo.push({ Indicador: 'Atenção', Valor: notaForaDosPostos(rec) });
  return [
    ['Resumo', resumo],
    ['Veículos por tipo', d.veiculos.por_tipo.map((v) => ({
      'Tipo de veículo': v.tipo, Registros: v.veiculos, 'Placas distintas': v.placas,
    }))],
    ['Placas', placas.map((p) => ({ Placa: p.placa, 'Tipo(s)': p.tipos, 'Abordagens no período': p.abordagens }))],
    ['Abordados por tipo', d.abordados.por_tipo.map((a) => ({
      Tipo: a.tipo, Papel: a.condutor ? 'Condutor' : 'Ajudante', Abordados: a.abordados, 'Marcados como condutor': a.dirigindo,
    }))],
    ['Empresas', empresas.map((e) => ({ CNPJ: formataCnpj(e.cnpj), Nome: e.nome, 'Abordagens no período': e.abordagens }))],
    ['Infrações por objeto', linhasDeObjeto(d.infracoes.por_grupo)],
    ['Quantidade por unidade', d.infracoes.volume_por_unidade.map((v) => ({
      Unidade: v.unidade, Quantidade: v.quantidade,
    }))],
    ['Infrações por razão', d.infracoes.por_razao.map((r) => ({
      'Razão da autuação': r.razao, Natureza: r.natureza, 'Ocorrências registradas': r.ocorrencias, 'Autuadas': r.autuadas,
    }))],
    ['Autos Receita por origem', rec.por_origem.map((o) => ({
      'Origem (RF)': o.origem, 'É posto do SIG': o.posto ? 'Sim' : 'Não', Autos: o.autos, 'Total (R$)': o.total,
    }))],
    ['Autos Receita por infração', rec.por_infracao.map((i) => ({
      'Código': i.codigo, 'Infração': i.descricao, Autos: i.autos, 'Total (R$)': i.total,
    }))],
  ];
}
